import {
    Button, Checkbox, Chip, FormControl, InputLabel, ListItemText,
    MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, TextField
} from "@mui/material";
import {Division} from "../../data/Division";
import {RankResponse} from "../../data/RankResponse";

export interface ObservationFilterState {
    query: string
    division: number | ""
    ranks: number[]
    from: string
    to: string
}

export const emptyObservationFilters: ObservationFilterState = {query: "", division: "", ranks: [], from: "", to: ""}

export function buildObservationSearchUrl(filters: ObservationFilterState, page: number): string {
    const params = new URLSearchParams()
    params.set("page", String(page))
    if (filters.query) params.set("query", filters.query)
    if (filters.division) params.set("division", String(filters.division))
    if (filters.ranks.length > 0) params.set("ranks", filters.ranks.join(","))
    if (filters.from) params.set("from", new Date(filters.from).toISOString())
    if (filters.to) {
        const to = new Date(filters.to)
        to.setHours(23, 59, 59, 999)
        params.set("to", to.toISOString())
    }
    return `/api/observation-report/search?${params.toString()}`
}

export function buildObservationStatsUrl(filters: ObservationFilterState): string {
    const params = new URLSearchParams()
    if (filters.query) params.set("query", filters.query)
    if (filters.division) params.set("division", String(filters.division))
    if (filters.ranks.length > 0) params.set("ranks", filters.ranks.join(","))
    if (filters.from) params.set("from", new Date(filters.from).toISOString())
    if (filters.to) {
        const to = new Date(filters.to)
        to.setHours(23, 59, 59, 999)
        params.set("to", to.toISOString())
    }
    return `/api/observation-report/stats?${params.toString()}`
}

export function isObservationFiltering(filters: ObservationFilterState): boolean {
    return filters.query !== "" || filters.division !== "" || filters.ranks.length > 0 || filters.from !== "" || filters.to !== ""
}

interface ObservationFilterPanelProps {
    filters: ObservationFilterState
    divisions: Division[]
    ranks: RankResponse[]
    onChange: (f: ObservationFilterState) => void
    onClear: () => void
}

export function ObservationFilterPanel({filters, divisions, ranks, onChange, onClear}: ObservationFilterPanelProps) {
    const rankDisplayMap = new Map(ranks.map(r => [r.rankId, r.displayName]))

    return <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: "wrap"}}>
        <TextField
            label="Search officer / supervisor"
            variant="outlined"
            size="small"
            sx={{minWidth: 220}}
            value={filters.query}
            onChange={e => onChange({...filters, query: e.target.value})}
        />
        <FormControl size="small" sx={{minWidth: 180}}>
            <InputLabel>Supervisor Division</InputLabel>
            <Select
                value={filters.division}
                label="Supervisor Division"
                onChange={(e: SelectChangeEvent<number | "">) => onChange({...filters, division: e.target.value as number | ""})}
            >
                <MenuItem value=""><em>Any</em></MenuItem>
                {divisions.filter(d => d.identifier !== "server_management").map(d => <MenuItem key={d.id} value={d.id}>{d.displayName}</MenuItem>)}
            </Select>
        </FormControl>
        <FormControl size="small" sx={{minWidth: 200}}>
            <InputLabel>Supervisor Rank</InputLabel>
            <Select
                multiple
                value={filters.ranks}
                label="Supervisor Rank"
                input={<OutlinedInput label="Supervisor Rank"/>}
                onChange={(e: SelectChangeEvent<number[]>) => {
                    const value = e.target.value
                    onChange({...filters, ranks: typeof value === "string" ? [] : value})
                }}
                renderValue={(selected) =>
                    <Stack direction="row" spacing={0.5} sx={{flexWrap: "wrap"}}>
                        {selected.map(id => <Chip key={id} label={rankDisplayMap.get(id) ?? id} size="small"/>)}
                    </Stack>
                }
            >
                {ranks.map(r => (
                    <MenuItem key={r.rankId} value={r.rankId}>
                        <Checkbox checked={filters.ranks.includes(r.rankId)} size="small"/>
                        <ListItemText primary={r.displayName}/>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
        <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{shrink: true}}
            value={filters.from}
            onChange={e => onChange({...filters, from: e.target.value})}
        />
        <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{shrink: true}}
            value={filters.to}
            onChange={e => onChange({...filters, to: e.target.value})}
        />
        {isObservationFiltering(filters) &&
            <Button size="small" color="warning" onClick={onClear}>Clear</Button>
        }
    </Stack>
}
